// Host-controller web Google sign-in (Path B).
//
// Used on a phone or any browser WITHOUT the Copresent extension, where Path A
// (reusing the extension's session) isn't possible. A standard browser PKCE
// authorization-code flow: we redirect to Google, Google redirects back to
// /auth/callback with a one-time code, and we POST that to the SAME backend
// endpoint the extension uses (/auth/google), which exchanges it (it holds the
// client secret) and returns a Copresent session token. The host page then
// JOINs with that token and the backend verifies it against the presentation's
// host account.

// Type-only, so there is no runtime cycle: usage.ts imports this module.
import type { PresentationAllowance } from "./usage";

const GOOGLE_AUTH_ENDPOINT = "https://accounts.google.com/o/oauth2/v2/auth";
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID ?? "";
const API_URL = process.env.REACT_APP_API_URL ?? "";

// Where the ACCOUNT lives, which is no longer where the live session lives.
//
// Sign-in, the lifecycle emails and billing moved to the Node
// service; the WebSocket, the deck resolve and slide previews stayed on Elastic
// Beanstalk. Two hostnames, split by domain rather than by path — the path
// split needed a Cloudflare Origin Rule whose Host override is Enterprise-only.
//
// The FULL url rather than a base, because the fallback is not just a different
// host: the C# endpoint is `/auth/google` and the new one is
// `/account/auth/google`, so a base-url fallback would build a path that
// exists on neither. A build with no account service configured therefore signs
// in against the old backend exactly as it always did.
//
// That fallback is also why the C# endpoint stays alive at all: the shipped
// extension posts to it directly, and a Web Store release is weeks away.
const SIGN_IN_URL = process.env.REACT_APP_QA_API_URL
  ? `${process.env.REACT_APP_QA_API_URL}/account/auth/google`
  : `${process.env.REACT_APP_API_URL ?? ""}/auth/google`;

// The controller is now the only place the host signs in, so this one grant has
// to cover both host identity (openid/email/profile → the `sub`) and live
// previews (drive.file → the backend renders thumbnails). drive.file is
// non-sensitive (no Google verification), and access only ever covers the
// specific deck the host later picks in the Picker.
const SCOPES = [
  "openid",
  "email",
  "profile",
  "https://www.googleapis.com/auth/drive.file",
].join(" ");

const SESSION_KEY = "copresentHostSession";
const VERIFIER_KEY = "copresentHostPkceVerifier";
const STATE_KEY = "copresentHostPkceState";
const RETURN_KEY = "copresentHostReturnTo";

export interface HostSession {
  sessionToken: string;
  profile: { email: string; name: string; picture: string };
}

/**
 * What the sign-in told us about the account, beyond the session itself.
 *
 * Kept OUT of HostSession, and so out of localStorage, because none of it is
 * durable: `isNewUser` is true of one moment and false forever after, and the
 * plan changes without any sign-in happening. Storing either would produce a
 * stale answer that looks authoritative — a returning host reading "new" for
 * as long as their session lasted.
 */
export interface SignInOutcome {
  isNewUser: boolean;
  plan: {
    isPro: boolean;
    planSource: "none" | "paid";
  } | null;
}

// The redirect URI registered on the Google OAuth web client. Same-origin so it
// follows whichever host the controller is served from.
export const redirectUri = (): string => `${window.location.origin}/auth/callback`;

export const isWebAuthConfigured = (): boolean => Boolean(CLIENT_ID);

export const getStoredHostSession = (): HostSession | null => {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as HostSession) : null;
  } catch {
    return null;
  }
};

// The signed-in host's Google `sub`, read out of the session token's payload.
//
// The sub isn't on HostSession.profile, but it IS the key the backend stores a
// plan under (users/{sub}) — so checkout needs it to say WHICH account paid.
// The token is `payloadB64.hmac`; we only read the payload, and we do NOT
// verify the signature. That's fine here: this value is used purely to label an
// outgoing Stripe checkout, never to prove identity. The backend re-derives the
// real sub from the token's HMAC on every request, so forging this buys nothing
// but paying for someone else's account.
export const getStoredHostSub = (): string | null => {
  const session = getStoredHostSession();
  if (!session?.sessionToken) return null;
  try {
    const payload = session.sessionToken.split(".")[0];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const claims = JSON.parse(window.atob(padded)) as { Sub?: string };
    return claims.Sub || null;
  } catch {
    return null;
  }
};

// Resolve a Google Slides deck (file) id to its CURRENT session's magicId.
// The host controller is keyed to the deck, not the rotating magic link, so it
// asks the backend which session is live before opening the WebSocket. Requires
// the host session token. Returns:
//   { magicId, inviteToken }
//                      — a live/recent session to connect to, plus the token the
//                        copresenter link should carry. inviteToken is undefined
//                        if the backend couldn't issue one; fall back to the
//                        magicId, which still joins.
//   "no_session"       — AUTHORITATIVE 404: deck has no current session (host
//                        hasn't started, or it's between sessions) → wait + retry
//   "unauthenticated"  — 401: no/invalid session token → controller shows sign-in
//   "unavailable"      — transient (5xx / network / non-JSON): the answer is
//                        unknown right now. The caller must NOT act on this — keep
//                        the current session and try again — so a backend blip or
//                        deploy can't flip the resolved magicId and thrash the WS.
//   "needs_host_link"  — 403: this deck has a host key and we didn't present it.
//                        NOT "not running" and NOT "sign in" — the fix is to
//                        open the controller from the extension's QR / link,
//                        which carries the key in its #k= fragment.
//   { limit }          — 402: this Free account has used its presentations for
//                        the month (and the limit is enforced). The controller
//                        shows the limit wall and keeps polling, so an upgrade
//                        or the 1st of the month lets it straight back in.
export type DeckSessionResult =
  | {
      magicId: string;
      inviteToken?: string;
      presentationAllowance?: PresentationAllowance | null;
    }
  | { limit: PresentationAllowance }
  | "no_session"
  | "needs_host_link"
  | "unauthenticated"
  | "unavailable";

export const resolveHostSession = async (
  deckId: string,
  // What to present instead of the stored account, when the caller has
  // something better for this deck — a phone paired from the QR has no account,
  // only a pairing (see devicePairing.ts). Omitted, the account is used.
  bearer?: string | null
): Promise<DeckSessionResult> => {
  const sessionToken =
    bearer !== undefined ? bearer : getStoredHostSession()?.sessionToken;
  // The account is the only host credential now. The extension-minted host key
  // that used to ride in X-Copresent-Host-Key is retired — the controller no
  // longer opens without a sign-in, so the credential it existed to serve (an
  // anonymous host on a QR-paired phone) no longer exists.
  //
  // Still not short-circuited to "unauthenticated" when there is no session:
  // the controller decides whether to call this at all, and letting the backend
  // answer keeps one authority on that rather than two.
  const headers: Record<string, string> = {};
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;

  try {
    // The browser's zone: the Free allowance counts presentations per local
    // day and resets at local midnight on the 1st.
    let tz: string | undefined;
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
      tz = undefined;
    }
    const query = tz ? `?tz=${encodeURIComponent(tz)}` : "";
    const resp = await fetch(
      `${API_URL}/host/deck/${encodeURIComponent(deckId)}/session${query}`,
      { headers }
    );
    if (resp.status === 401) return "unauthenticated";
    if (resp.status === 402) {
      const body = (await resp.json().catch(() => ({}))) as {
        used?: number;
        limit?: number;
        resetsAt?: string | null;
      };
      return {
        limit: {
          used: body.used ?? 0,
          limit: body.limit ?? 0,
          resetsAt: body.resetsAt ?? null,
          enforced: true,
          blocked: true,
        },
      };
    }
    if (resp.status === 403) return "needs_host_link";
    if (resp.status === 404) return "no_session";
    if (!resp.ok) return "unavailable"; // 5xx / transient — don't act on it
    const body = (await resp.json()) as {
      magicId?: string;
      inviteToken?: string;
      presentationAllowance?: PresentationAllowance | null;
    };
    return body.magicId
      ? {
          magicId: body.magicId,
          inviteToken: body.inviteToken,
          presentationAllowance: body.presentationAllowance,
        }
      : "no_session";
  } catch {
    // Network hiccup — unknown, not "no session". Keep the current session and
    // retry rather than tearing it down.
    return "unavailable";
  }
};

// "New link": revoke this deck's current invite and get a replacement.
//
// The session is untouched — that's the whole reason the invite is a separate
// token. Revoking used to mean rotating the magicId, which tore the room down
// and kicked out everyone already in it, so in practice nobody ever revoked
// anything and a leaked link stayed live until it idled out.
//
// Returns the new token, or null if the rotation didn't happen. Null must NOT
// be rendered as success: a host who believes a leaked link is dead when it
// isn't is worse off than one who knows the rotation failed.
export const rotateInviteToken = async (
  deckId: string,
  // As for resolveHostSession: a paired phone presents its pairing.
  bearer?: string | null
): Promise<string | null> => {
  const sessionToken =
    bearer !== undefined ? bearer : getStoredHostSession()?.sessionToken;
  const headers: Record<string, string> = {};
  if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;

  try {
    const resp = await fetch(
      `${API_URL}/host/deck/${encodeURIComponent(deckId)}/invite/rotate`,
      { method: "POST", headers }
    );
    if (!resp.ok) return null;
    const body = (await resp.json()) as { inviteToken?: string };
    return body.inviteToken ?? null;
  } catch {
    return null;
  }
};

export const clearHostSession = (): void => {
  try {
    window.localStorage.removeItem(SESSION_KEY);
    window.dispatchEvent(new Event("copresent-session-changed"));
  } catch {
    /* ignore */
  }
};

// --- PKCE helpers -----------------------------------------------------------

const bytesToBase64Url = (bytes: Uint8Array): string => {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const randomToken = (): string => {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return bytesToBase64Url(arr);
};

const sha256Base64Url = async (input: string): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return bytesToBase64Url(new Uint8Array(digest));
};

// Kick off the redirect to Google. `returnTo` is where the callback should send
// the user back (e.g. the /host/:magicId path) once signed in.
export const beginGoogleSignIn = async (returnTo: string): Promise<void> => {
  if (!CLIENT_ID) throw new Error("Google client ID not configured.");

  const codeVerifier = randomToken();
  const codeChallenge = await sha256Base64Url(codeVerifier);
  const state = randomToken();

  window.sessionStorage.setItem(VERIFIER_KEY, codeVerifier);
  window.sessionStorage.setItem(STATE_KEY, state);
  window.sessionStorage.setItem(RETURN_KEY, returnTo);

  const url = new URL(GOOGLE_AUTH_ENDPOINT);
  url.searchParams.set("client_id", CLIENT_ID);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("redirect_uri", redirectUri());
  url.searchParams.set("scope", SCOPES);
  url.searchParams.set("code_challenge", codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("state", state);
  // Offline access so the backend gets a refresh token it can use to render
  // slide previews later. prompt=consent guarantees the refresh token comes
  // back (Google only returns one when consent is freshly granted).
  url.searchParams.set("access_type", "offline");
  url.searchParams.set("prompt", "consent");
  url.searchParams.set("include_granted_scopes", "true");

  window.location.assign(url.toString());
};

// Run on the /auth/callback page. Validates state, exchanges the code via the
// backend, stores the session, and returns where to navigate next. Throws on
// any failure so the callback page can show an error.
//
// Memoised: React StrictMode double-invokes effects in dev, which would redeem
// the SINGLE-USE authorization code twice — the second exchange fails with
// invalid_grant ("exchange_failed"). Returning one shared in-flight promise
// guarantees the code is exchanged exactly once. The module instance survives
// the dev remount; a real new sign-in is a full page load through Google, which
// resets this naturally.
export interface SignInCompletion extends SignInOutcome {
  returnTo: string;
}

let exchangeOnce: Promise<SignInCompletion> | null = null;

export const completeGoogleSignIn = (): Promise<SignInCompletion> => {
  if (!exchangeOnce) exchangeOnce = runExchange();
  return exchangeOnce;
};

const runExchange = async (): Promise<SignInCompletion> => {
  const params = new URL(window.location.href).searchParams;
  const returnTo = window.sessionStorage.getItem(RETURN_KEY) || "/";

  const error = params.get("error");
  if (error) throw new Error(error);

  const code = params.get("code");
  const state = params.get("state");
  const expectedState = window.sessionStorage.getItem(STATE_KEY);
  const codeVerifier = window.sessionStorage.getItem(VERIFIER_KEY);

  if (!code) throw new Error("no_authorization_code");
  if (!state || state !== expectedState) throw new Error("state_mismatch");
  if (!codeVerifier) throw new Error("missing_verifier");

  // The ACCOUNT service, not the session backend — see SIGN_IN_URL.
  const resp = await fetch(SIGN_IN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, codeVerifier, redirectUri: redirectUri() }),
  });
  if (!resp.ok) {
    let detail = "";
    try {
      const body = await resp.json();
      detail = body?.error ? `: ${body.error}` : "";
    } catch {
      /* non-JSON */
    }
    throw new Error(`sign_in_failed_${resp.status}${detail}`);
  }

  window.sessionStorage.removeItem("copresentEmailTips");
  const body = (await resp.json()) as HostSession & Partial<SignInOutcome>;

  // Only the durable half is stored. See SignInOutcome for why isNewUser and
  // the plan are handed back to the caller instead.
  const session: HostSession = {
    sessionToken: body.sessionToken,
    profile: body.profile,
  };
  window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  window.dispatchEvent(new Event("copresent-session-changed"));

  // One-time PKCE material — clear it so a stale verifier can't be reused.
  window.sessionStorage.removeItem(VERIFIER_KEY);
  window.sessionStorage.removeItem(STATE_KEY);
  window.sessionStorage.removeItem(RETURN_KEY);

  return {
    returnTo,
    // Absent from the C# endpoint, which the shipped extension still uses and
    // which will never learn to send it. Defaulting to false is the safe way
    // round: a sign-up misreported as a sign-in loses one analytics
    // distinction, where the reverse would claim someone was new every time.
    isNewUser: body.isNewUser === true,
    plan: body.plan ?? null,
  };
};
